import React, { useState } from 'react';
import { Form, Input, Button, message, InputNumber, Select } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { ContractDataRow } from './ContractDataRow';
import { SectionHeader } from './SectionHeader';
import { GridLayout } from './GridLayout';
import { CardContainer } from './CardContainer';
import { Goods } from './types';
import api from '../../services/api';

const { TextArea } = Input;

// Unit options from schema
const UNIT_OPTIONS = [
  { value: 'CARTONS', label: 'Cartons' },
  { value: 'BAGS', label: 'Bags' },
  { value: 'MT', label: 'MT' },
  { value: 'KG', label: 'KG' },
  { value: 'PIECES', label: 'Pieces' },
];

interface GoodsCardProps {
  goods: Goods;
}

const GoodsCard: React.FC<GoodsCardProps> = ({ goods }) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Query commodities for dropdown
  const { data: commoditiesData, isLoading: commoditiesLoading, error: commoditiesError } = useQuery({
    queryKey: ['commodities'],
    queryFn: async () => {
      console.log('🔍 Fetching commodities for GoodsCard...');
      const data: any = await api.get('/commodities', { params: { limit: 200 } });
      console.log('✅ Commodities loaded:', data?.commodities?.length || 0, 'items');
      return data;
    },
    enabled: isEditing,
    retry: false,
  });

  const handleEdit = () => {
    // Initialize form with current values from API
    // Note: API returns some fields as numbers, some as formatted strings
    const parseNumericValue = (value: any): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') return parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
      return 0;
    };

    const formValues = {
      commodityCode: goods.details?.commodityCode || '',
      variety: goods.details?.variety || '',
      grade: goods.details?.grade || '',
      size: goods.details?.size || '',
      spec: goods.details?.spec || '',
      price: parseNumericValue(goods.details?.price),
      buyerPrice: goods.details?.buyerPrice ? parseNumericValue(goods.details.buyerPrice) : undefined,
      units: parseNumericValue(goods.details?.units),
      lbsEa: parseNumericValue(goods.details?.lbsEa),
      unit: goods.details?.unit || '',
      documents: goods.details?.documents || '',
    };

    console.log('📝 Initializing GoodsCard form with:', formValues);
    console.log('📦 Original goods.details:', goods.details);
    
    form.setFieldsValue(formValues);
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      const apiData: any = {};
      if (values.commodityCode !== undefined) apiData.commodity_code = values.commodityCode;
      if (values.variety !== undefined) apiData.variety = values.variety;
      if (values.grade !== undefined) apiData.grade = values.grade;
      if (values.size !== undefined) apiData.size = values.size;
      if (values.spec !== undefined) apiData.spec = values.spec;
      if (values.price !== undefined) apiData.price = values.price;
      if (values.buyerPrice !== undefined) apiData.buyer_price = values.buyerPrice;
      if (values.units !== undefined) apiData.num_units = values.units;
      if (values.lbsEa !== undefined) apiData.lbs_ea = values.lbsEa;
      if (values.unit !== undefined) apiData.unit = values.unit;
      // Note: documents field requires goodsDocumentTypeId lookup/creation, skipped for now
      
      await api.put(`/goods/${goods.id}`, apiData);
      
      message.success('Goods updated successfully');
      setIsEditing(false);
      window.location.reload();
    } catch (error: any) {
      message.error(error.message || 'Failed to update goods');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  return (
    <CardContainer variant="tertiary" style={{ marginTop: '24px' }}>
      <SectionHeader
        title={`Goods ${goods?.goodsNumber || 'Unknown'}`}
        actions={
          isEditing ? (
            <>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                onClick={handleSave}
                loading={saving}
                size="small"
              >
                Save
              </Button>
              <Button 
                icon={<CloseOutlined />} 
                onClick={handleCancel}
                disabled={saving}
                size="small"
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button 
              icon={<EditOutlined />} 
              onClick={handleEdit}
              size="small"
            >
              Edit
            </Button>
          )
        }
      />

      {isEditing ? (
        <Form form={form} layout="vertical">
          <GridLayout columns="1fr 1fr">
            <Form.Item label="Commodity" name="commodityCode">
              <Select
                showSearch
                placeholder="Select commodity"
                loading={commoditiesLoading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={commoditiesData?.commodities?.map((c: any) => ({
                  value: c.code,
                  label: `${c.name} (${c.code})`,
                }))}
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Form.Item label="Variety" name="variety" style={{ flex: 1, marginBottom: 0 }}>
                <Input placeholder="e.g. Nonpareil" />
              </Form.Item>
              <Form.Item label="Grade" name="grade" style={{ flex: 1, marginBottom: 0 }}>
                <Input placeholder="e.g. U.S. Fancy" />
              </Form.Item>
            </div>
          </GridLayout>

          <GridLayout columns="1fr 1fr">
            <Form.Item label="Size" name="size">
              <Input placeholder="e.g. 23/25" />
            </Form.Item>
            <Form.Item label="Spec" name="spec">
              <Input placeholder="e.g. J-spec" />
            </Form.Item>
          </GridLayout>

          <GridLayout columns="repeat(auto-fit, minmax(200px, 1fr))">
            <Form.Item label="Price ($)" name="price">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Price per unit"
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item>
            <Form.Item label="Buyer Price ($)" name="buyerPrice">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Buyer price per unit"
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item>
            <Form.Item label="Units" name="units">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Number of units"
                min={0}
              />
            </Form.Item>
            <Form.Item label="LBS EA." name="lbsEa">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Pounds each"
                min={0}
                step={0.01}
              />
            </Form.Item>
            <Form.Item label="Unit" name="unit">
              <Select
                placeholder="Select unit"
                options={UNIT_OPTIONS}
              />
            </Form.Item>
          </GridLayout>

          <Form.Item label="Documents" name="documents">
            <TextArea rows={2} placeholder="Enter document requirements" disabled />
          </Form.Item>
        </Form>
      ) : (
        <>
          <GridLayout columns="1fr 1fr">
            <ContractDataRow label="Commodity" value={goods.details?.commodity || 'N/A'} />
            <ContractDataRow label="Goods" value={goods.details?.goods || 'N/A'} />
          </GridLayout>

          {/* Show individual fields if they exist (for debugging/verification) */}
          {(goods.details?.variety || goods.details?.grade || goods.details?.size || goods.details?.spec) && (
            <GridLayout columns="repeat(auto-fit, minmax(200px, 1fr))">
              {goods.details?.variety && <ContractDataRow label="Variety" value={goods.details.variety} />}
              {goods.details?.grade && <ContractDataRow label="Grade" value={goods.details.grade} />}
              {goods.details?.size && <ContractDataRow label="Size" value={goods.details.size} />}
              {goods.details?.spec && <ContractDataRow label="Spec" value={goods.details.spec} />}
            </GridLayout>
          )}

          <GridLayout columns="repeat(auto-fit, minmax(200px, 1fr))">
            <ContractDataRow label="Price" value={goods.details?.price || 'N/A'} />
            {goods.details?.buyerPrice && (
              <ContractDataRow label="Buyer Price" value={goods.details.buyerPrice} />
            )}
            <ContractDataRow label="Units" value={goods.details?.units || 'N/A'} />
            <ContractDataRow label="LBS EA." value={goods.details?.lbsEa || 'N/A'} />
            <ContractDataRow label="Unit" value={goods.details?.unit || 'N/A'} />
          </GridLayout>

          <div style={{ width: '100%' }}>
            <ContractDataRow
              label="Documents"
              value={goods.details?.documents || 'N/A'}
            />
          </div>
        </>
      )}
    </CardContainer>
  );
};

export default GoodsCard;
